import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteLayout, useDocumentTitle } from '../../components/platform/SiteLayout';
import { Container, Heading, Card, Btn } from '../../components/platform/ui';
import useToolTask from '../../components/tools/useToolTask';
import { FilePicker, TaskProgress, DownloadButton, buildForm } from '../../components/tools/ToolShared';
import SegmentationViewer from '../../components/tools/SegmentationViewer';
import { instrumentsApi } from '../../services/toolsApi';
import { structureName, warningText, splitFiles, modelLabel, formatTeeth } from './instrumentsView';

const SAFE_NAME = /^[A-Za-z0-9_.-]{1,80}$/;

// Имена файлов из ответа сервиса: поддерживаем и список строк, и список объектов с полем name
const fileNames = (results) => {
  const raw = results?.files;
  if (!Array.isArray(raw)) return [];
  return raw.map((f) => (typeof f === 'string' ? f : f?.name || f?.filename)).filter((n) => n && SAFE_NAME.test(n));
};

const StructureTable = ({ structures, language, t }) => (
  <div className="mb-5 max-h-96 overflow-auto border-2 border-black">
    <table className="w-full text-base">
      <thead className="bg-black text-white sticky top-0">
        <tr><th className="text-left p-3">{t('pl.instruments.segment.structureName')}</th>
          <th className="text-right p-3">{t('pl.instruments.segment.structureVolume')}</th></tr>
      </thead>
      <tbody>
        {structures.map((s) => (
          <tr key={s.label} className="border-t border-black/20">
            <td className="p-3">{structureName(s, language)}</td>
            <td className="p-3 text-right tabular-nums">{Math.round(s.volume_mm3).toLocaleString(language)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ReportBlock = ({ report, language, t }) => {
  if (!report?.text) return null;
  const consistency = report.consistency;
  return (
    <div className="mb-6">
      <div className="text-lg font-bold mb-2">{t('pl.instruments.report.text')}</div>
      <pre className="whitespace-pre-wrap text-base border-2 border-black bg-white p-4 max-h-[32rem] overflow-auto" data-testid="report-text">
        {report.text}
      </pre>
      <p className="text-sm text-gray-600 mt-1">{t('pl.instruments.report.englishOnly')}</p>
      <div className="text-lg font-bold mt-5 mb-2">{t('pl.instruments.report.warningsTitle')}</div>
      {!consistency || consistency.checked === false ? (
        <p className="text-base text-gray-700">{t('pl.instruments.report.warningsNotChecked')}</p>
      ) : consistency.warnings?.length ? (
        <ul className="list-disc pl-6 text-base text-red-800 space-y-1" data-testid="report-warnings">
          {consistency.warnings.map((w, i) => <li key={`${w.type}-${i}`}>{warningText(w, language)}</li>)}
        </ul>
      ) : (
        <p className="text-base text-gray-700">{t('pl.instruments.report.warningsNone')}</p>
      )}
      <p className="text-sm text-gray-600 mt-1">{t('pl.instruments.report.warningsHint')}</p>
    </div>
  );
};

/** Полная сегментация КЛКТ; по галочке тот же прогон модели добавляет текстовое описание. Доступ только администратору. */
const InstrumentPage = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  useDocumentTitle(t('pl.instruments.segment.title'));
  const task = useToolTask('instruments.task.segment', instrumentsApi);
  const [files, setFiles] = useState([]);
  const [withReport, setWithReport] = useState(false);
  const busy = task.phase === 'uploading' || task.phase === 'processing';
  const names = useMemo(() => fileNames(task.results), [task.results]);
  const { main, models } = splitFiles(names, 'segment');
  const report = task.results?.report || null;
  const structures = useMemo(() => (Array.isArray(report?.structures) ? report.structures : []), [report]);
  const run = () => task.start('segment', buildForm({ files }, withReport ? { with_report: 'true' } : {}));

  return (
    <SiteLayout>
      <Container className="py-10 max-w-6xl">
        <Heading level={1} className="text-4xl mb-3">{t('pl.instruments.segment.title')}</Heading>
        <p className="mb-8 text-lg text-gray-700 max-w-4xl">{t('pl.instruments.segment.subtitle')}</p>

        <TaskProgress task={task} />

        <Card className="mb-8">
          <FilePicker files={files} onChange={setFiles} disabled={busy} />
          <label className="flex items-start gap-3 mb-5 cursor-pointer select-none">
            <input type="checkbox" className="mt-1 w-5 h-5 accent-[#C41E3A]" checked={withReport} disabled={busy}
                   onChange={(e) => setWithReport(e.target.checked)} data-testid="with-report" />
            <span>
              <span className="block text-lg font-bold">{t('pl.instruments.segment.withReport')}</span>
              <span className="block text-sm text-gray-600">{t('pl.instruments.segment.withReportHint')}</span>
            </span>
          </label>
          <div className="flex flex-wrap gap-3">
            <Btn disabled={busy || files.length === 0} onClick={run}>{t('pl.instruments.segment.start')}</Btn>
          </div>
          <p className="text-sm text-gray-500 mt-4">{t('pl.instruments.limits')}</p>
        </Card>

        {task.phase === 'done' && (
          <Card className="mb-8">
            <Heading level={2} className="text-2xl mb-4">{t('pl.instruments.result')}</Heading>
            {structures.length > 0 && (
              <>
                {report?.teeth_present_fdi?.length > 0 && (
                  <p className="text-base mb-4">{t('pl.instruments.segment.teethFound', { list: formatTeeth(report.teeth_present_fdi) })}</p>
                )}
                <SegmentationViewer api={instrumentsApi} taskId={task.taskId} fileNames={names}
                                    structures={structures} language={language} />
                <ReportBlock report={report} language={language} t={t} />
                <div className="text-lg font-bold mb-2">{t('pl.instruments.segment.structures')}</div>
                <StructureTable structures={structures} language={language} t={t} />
              </>
            )}
            {names.length === 0 ? (
              <p className="text-base text-gray-600">{t('pl.toolsPage.common.noFiles')}</p>
            ) : (
              <>
                <div className="text-lg font-bold mb-2">{t('pl.instruments.segment.filesMain')}</div>
                <div className="flex flex-wrap gap-3 mb-5">
                  {main.map((n) => <DownloadButton key={n} api={instrumentsApi} taskId={task.taskId} name={n} />)}
                </div>
                {models.length > 0 && (
                  <details className="mb-2">
                    <summary className="cursor-pointer text-base font-bold">{t('pl.instruments.segment.filesModels', { count: models.length })}</summary>
                    <p className="text-sm text-gray-600 my-2">{t('pl.instruments.segment.filesModelsHint')}</p>
                    <div className="flex flex-wrap gap-2">
                      {models.map((n) => (
                        <DownloadButton key={n} api={instrumentsApi} taskId={task.taskId} name={n}
                                        label={modelLabel(n, structures, language)} />
                      ))}
                    </div>
                  </details>
                )}
              </>
            )}
            <p className="text-sm text-gray-500 mt-4">{t('pl.toolsPage.common.retention')}</p>
          </Card>
        )}
        <p className="text-base font-bold text-grey-200">{t('pl.instruments.disclaimer')}</p>
      </Container>
    </SiteLayout>
  );
};

export const FullSegmentationPage = InstrumentPage;
export default InstrumentPage;
